import React, { useEffect, useRef, useState } from 'react';

import Layout from '../components/Layout';
import { gearData } from '../data/gearData';
import crosshairIcon from '../assets/images/routine-crosshair-icon.png';
import screenshotDrill from '../assets/images/screenshots/drill.png';
import screenshotTimer from '../assets/images/screenshots/timer.png';
import screenshotRoutine from '../assets/images/screenshots/routine.png';
import screenshotHistory from '../assets/images/screenshots/history.png';
import screenshotShoot from '../assets/images/screenshots/shoot.png';
import jacobAvatar from '../assets/images/jacob.png';
import instructorPhoto from '../assets/images/Instructor.png';
import heroPhoto from '../assets/images/hero.png';
import appleDownloadLogo from '../assets/images/apple-download-logo.svg';
import appStorePreview from '../assets/images/app_store.png';
import jacobTimerPhoto from '../assets/images/jacob_timer.png';
// Drop new PNGs in src/assets/images/screenshots/, import them above,
// then set `src` here. Caption is the label under each slide.
const GALLERY_ITEMS = [
  { caption: 'Lets Shoot!', src: screenshotShoot },
  { caption: 'Live Fire & Jacob AI', src: screenshotRoutine },
  { caption: 'Drill Library', src: screenshotDrill },
  { caption: 'Shooting Timer', src: screenshotTimer },
  { caption: 'Session History', src: screenshotHistory },
  // Add more like:
  // { caption: 'Learn & Tips', src: screenshotLearn },
  // { caption: 'Recommended Gear', src: screenshotGear },
];

const ABOUT_FEATURES = [
  {
    title: 'Structured Training Programs',
    body: 'No more wondering what to practice. Follow proven, step-by-step drills designed by a certified instructor for your skill level.',
  },
  {
    title: 'Track Real Progress',
    body: 'See your improvement in real-time. Watch your groupings tighten, your speed increase, and your confidence soar.',
  },
  {
    title: 'Build Safe Habits',
    body: 'Every drill reinforces proper safety protocols and firearm handling, building muscle memory for responsible gun ownership.',
  },
  {
    title: 'Achieve Your Goals',
    body: 'Whether you\'re training for competition, self-defense, or pure marksmanship, Range Drills gives you the roadmap to get there.',
  },
];

const PROGRAM_PILLS = [
  { title: 'Core Fundamentals', body: 'Grip, stance, sight alignment and trigger control.' },
  { title: 'Tactical Training', body: 'Movement and decision-making drills for real scenarios.' },
  { title: 'Dry-Fire Practice', body: 'Build reps at home: zero ammo, zero range trip.' },
  { title: 'Advanced Drills', body: 'Push speed and accuracy once fundamentals are locked in.' },
];

const WHY_FEATURES = [
  {
    title: 'Proven Results',
    body: 'Drills created by certified instructors using proven techniques from competitive shooters and law enforcement. You\'re not just practicing. You\'re training like a pro.',
  },
  {
    title: 'Save Money',
    body: 'Stop wasting ammunition on unfocused practice. Every round counts when you\'re following a structured training plan. Train smarter, not harder.',
  },
  {
    title: 'Save Time',
    body: 'No more wondering what to practice or how to improve. Get a clear training plan the moment you arrive at the range. More shooting, less guessing.',
  },
  {
    title: 'Track Progress',
    body: 'See real improvement over time. Track your sessions, review your performance, and watch your skills grow with measurable results.',
  },
  {
    title: 'Safety First',
    body: 'Every drill reinforces proper safety protocols and responsible firearm handling. Build the right habits from day one.',
  },
  {
    title: 'Always Available',
    body: 'Your personal shooting coach fits in your pocket. Train whenever, wherever, at your own pace. No scheduling, no expensive hourly rates.',
  },
];

const STRIP_ITEMS = [
  'CORE FUNDAMENTALS',
  'TACTICAL TRAINING',
  'DRY-FIRE PRACTICE',
  'ADVANCED DRILLS',
];

function GalleryCarousel() {
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(3);
  const [offset, setOffset] = useState(0);
  const viewportRef = useRef(null);

  useEffect(() => {
    const updatePerView = () => {
      setPerView(window.matchMedia('(max-width: 760px)').matches ? 1 : 3);
    };
    updatePerView();
    window.addEventListener('resize', updatePerView);
    return () => window.removeEventListener('resize', updatePerView);
  }, []);

  const pageCount = Math.max(1, Math.ceil(GALLERY_ITEMS.length / perView));

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  useEffect(() => {
    const measure = () => {
      if (!viewportRef.current) return;
      const gap = perView === 1 ? 16 : 22;
      setOffset(page * (viewportRef.current.offsetWidth + gap));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [page, perView]);

  return (
    <div className="gallery-carousel" data-carousel>
      <button
        type="button"
        className="gallery-carousel__btn gallery-carousel__btn--prev"
        aria-label="Previous screenshots"
        disabled={page === 0}
        onClick={() => setPage((p) => Math.max(0, p - 1))}
      >
        <span aria-hidden="true">‹</span>
      </button>

      <div className="gallery-carousel__viewport" data-carousel-viewport ref={viewportRef}>
        <div
          className="gallery"
          data-carousel-track
          style={{ transform: `translateX(-${offset}px)` }}
        >
          {GALLERY_ITEMS.map((item) => (
            <figure className="gallery__item" key={item.caption}>
              {item.src ? (
                <div className="gallery__frame">
                  <span className="tag-flag" />
                  <img src={item.src} alt={item.caption} className="gallery__img" />
                </div>
              ) : (
                <div className="ph ph--gallery" data-ph={item.ph || item.caption}>
                  <span className="tag-flag" />
                </div>
              )}
              <figcaption>{item.caption}</figcaption>
            </figure>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="gallery-carousel__btn gallery-carousel__btn--next"
        aria-label="Next screenshots"
        disabled={page >= pageCount - 1}
        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
      >
        <span aria-hidden="true">›</span>
      </button>

      <div className="gallery-carousel__dots" data-carousel-dots role="tablist" aria-label="Screenshot pages">
        {Array.from({ length: pageCount }).map((_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            className={`gallery-carousel__dot${index === page ? ' is-active' : ''}`}
            aria-label={`Go to page ${index + 1}`}
            aria-selected={index === page}
            onClick={() => setPage(index)}
          />
        ))}
      </div>
    </div>
  );
}

function GearSection() {
  const categories = gearData.categories;
  const [activeCategory, setActiveCategory] = useState(categories[0]?.category || '');

  return (
    <section className="section section--texture" id="gear">
      <div className="container">
        <div className="section__head">
          <h2>
            Recommended <span className="text-accent">Gear</span>
          </h2>
          <p className="section__sub section__sub--center">
            The right gear doesn&apos;t just make training more comfortable. It makes you a better, safer shooter. Every item on this list has been personally tested and proven in real training sessions.
          </p>
        </div>

        <div className="gear-tabs" id="gearTabs">
          {categories.map((category) => (
            <button
              key={category.category}
              type="button"
              className={`gear-tab${activeCategory === category.category ? ' is-active' : ''}`}
              data-category={category.category}
              onClick={() => setActiveCategory(category.category)}
            >
              {category.category}
            </button>
          ))}
        </div>

        <div className="gear-panels" id="gearPanels">
          {categories.map((category) => (
            <div
              key={category.category}
              className={`gear-grid${activeCategory === category.category ? ' is-active' : ''}`}
              data-category={category.category}
            >
              {category.items.map((item) => {
                const card = (
                  <>
                    <div className="ph ph--gear" data-ph="Product photo" />
                    <div className="gear-card__body">
                      <h4>{item.name}</h4>
                      <p>{item.description}</p>
                      <span className="gear-card__price">{item.price_range}</span>
                    </div>
                  </>
                );

                if (item.affiliate_link) {
                  return (
                    <a
                      key={item.name}
                      className="gear-card"
                      href={item.affiliate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {card}
                    </a>
                  );
                }

                return (
                  <article key={item.name} className="gear-card">
                    {card}
                  </article>
                );
              })}
            </div>
          ))}
        </div>

        <p className="gear-disclaimer">
          As an Amazon Associate, Range Drills earns from qualifying purchases. We only recommend products we believe will enhance your training experience and have been personally tested and used by us.
        </p>
      </div>
    </section>
  );
}

const IndexPage = () => (
  <Layout>
    <main id="top">
        <section className="hero">
          <div className="container hero__inner">
            <div className="hero__copy">
              <p className="eyebrow">
                <span className="eyebrow__dot" />
                Stop Wasting Time At The Range
              </p>
              <h1>
                Shoot Better,
                <br />
                Hit Your <span className="text-accent">Mark.</span>
              </h1>
              <p className="hero__sub">
                Range Drills turns your range time into structured training: proven drills used by competitive shooters and law enforcement, real progress tracking, and an AI coach that tells you exactly what to fix. Stop guessing. Start progressing.
              </p>
            </div>

            <div className="hero__art">
              <div className="hero__frame">
                <img
                  src={heroPhoto}
                  alt="Shooter mid-drill at the range"
                  className="hero__img"
                />

                <span className="chip chip--float chip--tl">
                  <span className="chip__icon">
                    <img src={crosshairIcon} alt="" />
                  </span>
                  <span className="chip__num">4</span>
                  <span className="chip__label">Skill Levels</span>
                </span>
                <span className="chip chip--float chip--tr">
                  <span className="chip__icon">
                    <img src={crosshairIcon} alt="" />
                  </span>
                  <span className="chip__num">02:14</span>
                  <span className="chip__label">Par Time</span>
                </span>
                <span className="chip chip--float chip--br">
                  <span className="chip__icon">
                    <img src={crosshairIcon} alt="" />
                  </span>
                  <span className="chip__num">AI</span>
                  <span className="chip__label">Coaching</span>
                </span>
              </div>

              <div className="hero__proof">
                <span className="avatar-stack">
                  <span className="avatar-stack__item" />
                  <span className="avatar-stack__item" />
                  <span className="avatar-stack__item" />
                </span>
                <div className="hero__proof-text">
                  <strong>1,000s</strong>
                  Shooters Training
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="strip">
          <div className="container strip__inner">
            {[...STRIP_ITEMS, ...STRIP_ITEMS].map((item, index) => (
              <React.Fragment key={`${item}-${index}`}>
                {index > 0 && <span className="strip__sep">/</span>}
                <span className="strip__item">{item}</span>
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="section" id="about">
          <div className="container">
            <div className="section__head">
              <h2>
                Your Personal Shooting Coach,
                <br />
                <span className="text-accent">In Your Pocket</span>
              </h2>
              <p className="section__sub section__sub--center">
                Imagine having a professional instructor with you at every range session, guiding your drills, tracking your improvement, and pushing you to reach new levels of accuracy and confidence. That&apos;s exactly what Range Drills delivers:
              </p>
            </div>

            <div className="panel">
              <div className="panel__features">
                {ABOUT_FEATURES.map((feature) => (
                  <div className="panel__feature" key={feature.title}>
                    <span className="panel__icon">
                      <img src={crosshairIcon} alt="" />
                    </span>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src={instructorPhoto}
                alt="Range Drills instructor at the range"
                className="panel__img"
              />
            </div>
            <p className="panel__closing">Don&apos;t just shoot. Train with purpose. Make every round count.</p>
          </div>
        </section>

        <section className="section section--tight section--texture" id="drills">
          <div className="container">
            <div className="section__head">
              <p className="eyebrow eyebrow--center">
                <span className="eyebrow__dot" />
                Discover
              </p>
              <h2>
                What Sets Range Drills <span className="text-accent">Apart</span>
              </h2>
            </div>

            <div className="pills">
              {PROGRAM_PILLS.map((pill) => (
                <article className="pill" key={pill.title}>
                  <h3>{pill.title}</h3>
                  <p>{pill.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section__head">
              <h2>
                See The Difference:
                <br />
                <span className="text-accent">From Novice To Marksman</span>
              </h2>
              <p className="section__sub section__sub--center">
                These app screens show you exactly how we transform your training: structured sessions, real-time tracking, and expert guidance that&apos;s always available when you need it.
              </p>
            </div>

            <GalleryCarousel />
            <p className="reviews__note">This isn&apos;t just an app. It&apos;s your competitive advantage at the range.</p>
          </div>
        </section>

        <section className="section section--tight section--texture" id="why-us">
          <div className="container">
            <div className="section__head">
              <h2>
                Why Thousands Of Shooters
                <br />
                <span className="text-accent">Choose Range Drills</span>
              </h2>
            </div>

            <div className="features-grid">
              {WHY_FEATURES.map((feature) => (
                <article className="feature-card" key={feature.title}>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>

            <div className="callout">
              <h3>The Question Isn&apos;t &quot;Should I Get Range Drills?&quot;</h3>
              <p>It&apos;s: &quot;How much longer will I waste time and money at the range without real improvement?&quot;</p>
            </div>
          </div>
        </section>

        <section className="section" id="jacob">
          <div className="container">
            <div className="section__head">
              <h2>
                Experience
                <br />
                Training <span className="text-accent">Like Never Before</span>
              </h2>
            </div>

            <div className="spotlight">
              <article className="spotlight__card spotlight__card--a">
                <div className="spotlight__media">
                  <img
                    src={jacobTimerPhoto}
                    alt="Shot timer in use at the range"
                    className="spotlight__img"
                  />
                  <span className="spotlight__pill">Shot Timer</span>
                  <span className="spotlight__stat">±0.1s</span>
                </div>
                <div className="spotlight__body">
                  <h3>Par Time Precision</h3>
                  <p>Custom durations, reps and rest, paced exactly to your drill spec.</p>
                  <a href="#drills" className="link-arrow">Read More →</a>
                </div>
              </article>
              <article className="spotlight__card spotlight__card--b">
                <div className="spotlight__media">
                  <img
                    src={jacobAvatar}
                    alt="Jacob AI coach"
                    className="spotlight__img"
                  />
                  <span className="spotlight__pill">AI Coach</span>
                  <span className="spotlight__stat">1:1 Tips</span>
                </div>
                <div className="spotlight__body">
                  <h3>Meet Jacob</h3>
                  <p>Save a session and get one clear, personalized fix. No fluff.</p>
                  <a href="#jacob" className="link-arrow">Read More →</a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <GearSection />

        <section className="cta" id="download">
          <div className="container cta__panel">
            <div className="cta__copy">
              <h2>
                Start Your
                <br />
                Transformation Today
              </h2>
              <p>
                Don&apos;t let another range session pass without real progress. Join thousands of shooters who have already elevated their skills with Range Drills, whether you&apos;re preparing for competition, training for self-defense, or pursuing precision marksmanship.
              </p>
              <div className="cta__stores">
                <a
                  href="https://apps.apple.com/us/app/range-drills/id6738703801"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-btn store-btn--badge"
                >
                  <img
                    src={appleDownloadLogo}
                    alt="Download on the App Store"
                    className="store-btn__badge"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=io.idodev.rangedrills"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-btn store-btn--badge"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/GetItOnGooglePlay_Badge_Web_color_English.svg`}
                    alt="Get it on Google Play"
                    className="store-btn__badge"
                  />
                </a>
              </div>
              <div className="cta__connect">
                <span>Stay Connected</span>
                <a
                  href="https://www.instagram.com/rangedrillsio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  @rangedrillsio
                </a>
                <a href="mailto:info@rangedrills.io">info@rangedrills.io</a>
              </div>
            </div>
            <img
              src={appStorePreview}
              alt="Range Drills app preview on iPhone"
              className="cta__img"
            />
          </div>
        </section>
      </main>
    </Layout>
);

export default IndexPage;
